import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Layout from "pages/shared/Layout";

import { StoreContext } from "shared/context/store-context";
import { criminalRecordTypes, graduationTypes } from "shared/util/types";
import { formatDate } from "shared/util/time";
import { PERMISSION_TYPES } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";

import InventoryDriverItem from "./InventoryDriverItem";

const InventoryDrivers = ({ employmentFilter }) => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const itemType = "driverdetail";
  const listName =
    employmentFilter === "company"
      ? "company_drivers"
      : employmentFilter === "external"
        ? "external_drivers"
        : "inventory_drivers";

  const { driverDetails, drivers, DeleteDriverDetail, UpdateDriverDetail, InsertDriverDetail, vehicles } =
    useContext(StoreContext);

  const [pageItems, setPageItems] = useState();

  const shouldHighlightDate = (date) => {
    if (!date) {
      return true;
    }
    const currentDate = new Date();
    const oneMonthAfter = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
    return date <= formatDate(oneMonthAfter).split(" ")[0];
  };

  useEffect(() => {
    if (!driverDetails) {
      setPageItems([]);
      return;
    }

    if (employmentFilter === "company") {
      // Şirket sürücüleri - company olanlar veya employmentType tanımlı olmayanlar (varsayılan olarak company)
      setPageItems(driverDetails.filter((driver) => driver.employmentType === "company" || !driver.employmentType));
    } else if (employmentFilter === "external") {
      // Diğer sürücüler - external olanlar
      setPageItems(driverDetails.filter((driver) => driver.employmentType === "external"));
    } else {
      // Filtre yoksa tümünü göster
      setPageItems(driverDetails);
    }
  }, [driverDetails, employmentFilter]);

  const criminalRecordOptions = criminalRecordTypes.map((type) => ({
    value: type.value,
    label: t(`inventoryDriver.criminalRecord.${type.value}`),
  }));

  const graduationOptions = graduationTypes.map((type) => ({
    value: type.value,
    label: t(`inventoryDriver.graduation.${type.value}`),
  }));

  const listColumns = [
    {
      field: "driver",
      headerName: t("inventoryDriver.columns.driver"),
      width: 300,
      valueGetter: (params) => drivers.find((user) => user?._id === params?.row?.driver)?.name || "",
    },
    {
      field: "phonenumber",
      headerName: t("inventoryDriver.columns.phonenumber"),
      width: 150,
      valueGetter: (params) => drivers.find((user) => user?._id === params?.row?.driver)?.phonenumber || "",
    },
    {
      field: "vehicle",
      headerName: t("inventoryDriver.columns.vehicle"),
      width: 150,
      valueGetter: (params) => vehicles.find((vehicle) => vehicle.driver === params?.row?.driver)?.plate || "-",
    },

    {
      field: "isActive",
      headerName: t("inventoryDriver.columns.isActive"),
      width: 100,
      valueGetter: (params) => (params?.row?.isActive ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
    },
    // {
    //   field: "employmentType",
    //   headerName: "İstihdam Tipi",
    //   width: 150,
    //   valueGetter: (params) =>
    //     driverEmploymentTypes.find((type) => type.value === params?.row?.employmentType)?.label || "Şirket Sürücüsü",
    // },
    { field: "department", headerName: t("inventoryDriver.columns.department"), width: 200 },
    {
      field: "identity",
      headerName: t("inventoryDriver.columns.identity"),
      width: 150,
    },
    { field: "iban", headerName: t("inventoryDriver.columns.iban"), width: 200 },

    { field: "startDate", headerName: t("inventoryDriver.columns.startDate"), width: 150 },
    { field: "endDate", headerName: t("inventoryDriver.columns.endDate"), width: 150 },
    { field: "birthDate", headerName: t("inventoryDriver.columns.birthDate"), width: 150 },
    { field: "license", headerName: t("inventoryDriver.columns.license"), width: 100 },

    { field: "address", headerName: t("inventoryDriver.columns.address"), width: 200 },

    { field: "secondContactName", headerName: t("inventoryDriver.columns.secondContactName"), width: 150 },
    { field: "secondContactPhone", headerName: t("inventoryDriver.columns.secondContactPhone"), width: 150 },
    { field: "secondContactRelation", headerName: t("inventoryDriver.columns.secondContactRelation"), width: 150 },

    { field: "companySgkRegistration", headerName: t("inventoryDriver.columns.companySgkRegistration"), width: 200 },
    { field: "supplierSgkRegistration", headerName: t("inventoryDriver.columns.supplierSgkRegistration"), width: 200 },
    {
      field: "criminalRecord",
      headerName: t("inventoryDriver.columns.criminalRecord"),
      width: 100,
      valueGetter: (params) =>
        criminalRecordOptions.find((type) => type.value === params?.row?.criminalRecord)?.label || "",
    },
    { field: "criminalRecordDate", headerName: t("inventoryDriver.columns.criminalRecordDate"), width: 150 },
    { field: "identityExpirationDate", headerName: t("inventoryDriver.columns.identityExpirationDate"), width: 150 },
    { field: "srcCertificate", headerName: t("inventoryDriver.columns.srcCertificate"), width: 100 },
    { field: "srcCertificateStartDate", headerName: t("inventoryDriver.columns.srcCertificateStartDate"), width: 150 },
    { field: "licenseInquiry", headerName: t("inventoryDriver.columns.licenseInquiry"), width: 150 },
    { field: "licenseInquiryDate", headerName: t("inventoryDriver.columns.licenseInquiryDate"), width: 150 },
    {
      field: "graduation",
      headerName: t("inventoryDriver.columns.graduation"),
      width: 150,
      valueGetter: (params) => graduationOptions.find((type) => type.value === params?.row?.graduation)?.label || "-",
    },
    {
      field: "psychotechnicEndDate",
      headerName: t("inventoryDriver.columns.psychotechnicEndDate"),
      width: 150,
      cellClassName: (params) => (shouldHighlightDate(params?.row?.psychotechnicEndDate) ? "highlight-red" : undefined),
    },
    {
      field: "licenseExpirationDate",
      headerName: t("inventoryDriver.columns.licenseExpirationDate"),
      width: 150,
      cellClassName: (params) =>
        shouldHighlightDate(params?.row?.licenseExpirationDate) ? "highlight-red" : undefined,
    },

    {
      field: "licensePhoto",
      headerName: t("inventoryDriver.columns.licensePhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.licensePhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.licensePhoto ? undefined : "highlight-red"),
    },
    {
      field: "identityPhoto",
      headerName: t("inventoryDriver.columns.identityPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.identityPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.identityPhoto ? undefined : "highlight-red"),
    },
    {
      field: "criminalRecordPhoto",
      headerName: t("inventoryDriver.columns.criminalRecordPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.criminalRecordPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.criminalRecordPhoto ? undefined : "highlight-red"),
    },
    {
      field: "srcCertificatePhoto",
      headerName: t("inventoryDriver.columns.srcCertificatePhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.srcCertificatePhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.srcCertificatePhoto ? undefined : "highlight-red"),
    },
    {
      field: "psychotechnicPhoto",
      headerName: t("inventoryDriver.columns.psychotechnicPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.psychotechnicPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.psychotechnicPhoto ? undefined : "highlight-red"),
    },
    // {
    //   field: "iskurEmploymentRegistryPhoto",
    //   headerName: "İşkur Foto",
    //   width: 150,
    //   valueGetter: (params) => (params?.row?.iskurEmploymentRegistryPhoto ? "Evet" : "Hayır"),
    //   cellClassName: (params) => (params?.row?.iskurEmploymentRegistryPhoto ? undefined : "highlight-red"),
    // },
    // {
    //   field: "identityRegistryPhoto",
    //   headerName: "Kimlik Kayıt Foto",
    //   width: 150,
    //   valueGetter: (params) => (params?.row?.identityRegistryPhoto ? "Evet" : "Hayır"),
    //   cellClassName: (params) => (params?.row?.identityRegistryPhoto ? undefined : "highlight-red"),
    // },
    {
      field: "photo",
      headerName: t("inventoryDriver.columns.photo"),
      width: 150,
      valueGetter: (params) => (params?.row?.photo ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.photo ? undefined : "highlight-red"),
    },
    // {
    //   field: "militaryStatusPhoto",
    //   headerName: "Askerlik Foto",
    //   width: 150,
    //   valueGetter: (params) => (params?.row?.militaryStatusPhoto ? "Evet" : "Hayır"),
    //   cellClassName: (params) => (params?.row?.militaryStatusPhoto ? undefined : "highlight-red"),
    // },
    {
      field: "healthReportPhoto",
      headerName: t("inventoryDriver.columns.healthReportPhoto"),
      width: 150,
      valueGetter: (params) => (params?.row?.healthReportPhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no")),
      cellClassName: (params) => (params?.row?.healthReportPhoto ? undefined : "highlight-red"),
    },
    {
      field: "graduationCertificatePhoto",
      headerName: t("inventoryDriver.columns.graduationCertificatePhoto"),
      width: 150,
      valueGetter: (params) =>
        params?.row?.graduationCertificatePhoto ? t("inventoryCommon.yes") : t("inventoryCommon.no"),
      cellClassName: (params) => (params?.row?.graduationCertificatePhoto ? undefined : "highlight-red"),
    },
    { field: "notes", headerName: t("inventoryDriver.columns.notes"), width: 200 },
  ];

  const standardHelpItems = [
    t("inventoryDriver.help.standard1"),
    t("inventoryDriver.help.standard2"),
    t("inventoryDriver.help.standard3"),
    t("inventoryDriver.help.standard4"),
  ];

  const highlightedHelpItems = [
    t("inventoryDriver.help.highlighted1"),
    t("inventoryDriver.help.highlighted2"),
    t("inventoryDriver.help.highlighted3"),
    t("inventoryDriver.help.highlighted4"),
    t("inventoryDriver.help.highlighted5"),
    t("inventoryDriver.help.highlighted6"),
  ];

  return (
    <Layout
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      showSelectedYear={false}
      isAlwaysFullScreen={true}
      hideCreateNewItem={!auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY]}
      SelectedItemComponent={InventoryDriverItem}
      NewItemComponent={InventoryDriverItem}
      DeleteItem={DeleteDriverDetail}
      InsertItem={InsertDriverDetail}
      UpdateItem={UpdateDriverDetail}
      standardHelpItems={standardHelpItems}
      highlightedHelpItems={highlightedHelpItems}
    />
  );
};

export default InventoryDrivers;
